import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Bidirectional / account-reuse (GUNDI-5595): action configurations are stored on
// the INTEGRATION record, not on the Route, so every Route using this account
// inherits changes made here. Field mappings are NOT affected — those live on the
// Route and stay per-flow.
type SharedAccountNoticeProps = {
    accountName: string;
    // name is absent when the user can't resolve the Route (e.g. it lives in
    // a workspace they can't see) — those render as a count, never as ids/links.
    usedBy: { id: string, name?: string }[];
};

const SharedAccountNotice = ({ accountName, usedBy }: SharedAccountNoticeProps) => {
    const { t } = useTranslation();
    if (usedBy.length === 0) return null;
    const named = usedBy.filter((c) => c.name);
    const hiddenCount = usedBy.length - named.length;
    return (
        <div className="mb-5 rounded border border-orange-200 bg-orange-50 px-4 py-3">
            <div className="text-sm text-off-black">
                <span className="font-semibold">{t("Shared account.")}</span>{" "}
                {usedBy.length === 1
                    ? t("{{name}} is also used by another Route.", { name: accountName })
                    : t("{{name}} is also used by {{count}} other Routes.", { name: accountName, count: usedBy.length })}
                {" "}
                {t("Credentials and action settings below are stored on the account, so changes apply everywhere it is used. Field mappings are per Route and are not affected.")}
            </div>
            {(named.length > 0 || hiddenCount > 0) && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                    {named.map((c) => (
                        <Link key={c.id} to={`/routes/${c.id}`}
                            className="text-route-green text-sm font-semibold underline underline-offset-2">
                            {c.name} ▸
                        </Link>
                    ))}
                    {hiddenCount > 0 && (
                        <span className="text-sm text-secondary-text">
                            {hiddenCount === 1
                                ? t("{{count}} Route in a workspace you can't view", { count: hiddenCount })
                                : t("{{count}} Routes in workspaces you can't view", { count: hiddenCount })}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

export default SharedAccountNotice;
